import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Search, X } from 'lucide-react';
import { useMapStore } from '@/stores/map-store';
import { searchPlaces } from '@/map/search/search-registry';
import type { SearchResult } from '@/map/search/search-provider';
import { cn } from '@/lib/utils';

/** Debounce before issuing a search (covers the async geocoder lookup + its usage policy). */
const DEBOUNCE_MS = 500;

/** Top-center search HUD: places, coordinates, and live tracks → fly-to. */
export function SearchBar(): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const seqRef = useRef(0);

  useEffect(() => {
    if (query.trim().length === 0) {
      setResults([]);
      return;
    }
    const handle = setTimeout(() => void run(query, seqRef, setResults), DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [query]);

  function pick(result: SearchResult): void {
    useMapStore.getState().setFlyTo({ lng: result.lon, lat: result.lat, zoom: result.zoom });
    setOpen(false);
    setQuery('');
    setResults([]);
  }

  return (
    <div className="absolute left-1/2 top-3 z-[1000] -translate-x-1/2">
      {open ? (
        <SearchPanel
          query={query}
          results={results}
          onQuery={setQuery}
          onPick={pick}
          onClose={() => setOpen(false)}
        />
      ) : (
        <SearchChip onClick={() => setOpen(true)} />
      )}
    </div>
  );
}

/** Resolve a query and publish results only if it is still the latest request. */
async function run(
  query: string,
  seqRef: React.RefObject<number>,
  setResults: (results: SearchResult[]) => void,
): Promise<void> {
  const seq = (seqRef.current ?? 0) + 1;
  seqRef.current = seq;
  const found = await searchPlaces(query);
  if (seqRef.current === seq) setResults(found);
}

/** Collapsed search chip. */
function SearchChip({ onClick }: { onClick: () => void }): React.JSX.Element {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.14 }}
      className="flex items-center gap-2 rounded-lg border border-echo-border bg-echo-grey/90 px-3 py-2 shadow-lg backdrop-blur transition-colors hover:border-echo-border/80"
    >
      <Search className="h-4 w-4 text-echo-yellow" />
      <span className="font-chakra text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Search
      </span>
    </motion.button>
  );
}

/** Expanded search panel: input + grouped results. */
function SearchPanel({
  query,
  results,
  onQuery,
  onPick,
  onClose,
}: {
  query: string;
  results: SearchResult[];
  onQuery: (value: string) => void;
  onPick: (result: SearchResult) => void;
  onClose: () => void;
}): React.JSX.Element {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96, y: -4 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.16, ease: 'easeOut' }}
      className="w-80 overflow-hidden rounded-lg border border-echo-border bg-echo-grey/92 shadow-2xl ring-1 ring-black/20 backdrop-blur"
    >
      <div className="flex items-center gap-2 px-3 py-2">
        <Search className="h-4 w-4 shrink-0 text-echo-yellow" />
        <input
          autoFocus
          value={query}
          onChange={(event) => onQuery(event.target.value)}
          onKeyDown={(event) => event.key === 'Escape' && onClose()}
          placeholder="Search address or 38.89,-77.03…"
          aria-label="Search the map"
          className="flex-1 bg-transparent font-chakra text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none"
        />
        <button type="button" onClick={onClose} aria-label="Close search" className="text-muted-foreground hover:text-foreground">
          <X className="h-4 w-4" />
        </button>
      </div>
      {results.length > 0 && (
        <>
          <div className="h-px bg-gradient-to-r from-transparent via-echo-border to-transparent" />
          <ResultsList results={results} onPick={onPick} />
        </>
      )}
    </motion.div>
  );
}

/** Results grouped by provider source. */
function ResultsList({
  results,
  onPick,
}: {
  results: SearchResult[];
  onPick: (result: SearchResult) => void;
}): React.JSX.Element {
  return (
    <div className="max-h-[50vh] overflow-y-auto p-1">
      {groupResults(results).map(([source, group]) => (
        <div key={source}>
          <div className="px-2 pb-0.5 pt-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            {source}
          </div>
          {group.map((result, index) => (
            <ResultRow key={`${source}-${index}`} result={result} onClick={() => onPick(result)} />
          ))}
        </div>
      ))}
    </div>
  );
}

/** A single result row. */
function ResultRow({ result, onClick }: { result: SearchResult; onClick: () => void }): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full flex-col rounded-md px-2 py-1.5 text-left transition-colors hover:bg-echo-yellow/10"
    >
      <span className="truncate text-sm text-foreground">{result.label}</span>
      {result.sublabel && (
        <span className="truncate text-[11px] text-muted-foreground">{result.sublabel}</span>
      )}
    </button>
  );
}

/** Group results by their source label, preserving first-seen order. */
function groupResults(results: SearchResult[]): [string, SearchResult[]][] {
  const groups = new Map<string, SearchResult[]>();
  for (const result of results) {
    const list = groups.get(result.source) ?? [];
    list.push(result);
    groups.set(result.source, list);
  }
  return [...groups];
}
